import type { IconName } from "@/components/ui/icon";
import {
  TalentPoolResponseColor,
  TalentPoolResponseIcon,
  type TalentPoolResponse,
} from "@/lib/api/model";

export type PoolIconKey = NonNullable<TalentPoolResponseIcon>;
export type PoolColorKey = NonNullable<TalentPoolResponseColor>;

/** Every key the API accepts, in picker order. */
export const POOL_ICON_KEYS = Object.values(TalentPoolResponseIcon);
export const POOL_COLOR_KEYS = Object.values(TalentPoolResponseColor);

/** Pool icon key → central icon. */
export const POOL_ICONS: Record<PoolIconKey, IconName> = {
  users: "IconPeople",
  briefcase: "IconSuitcaseWork",
  building: "IconBuildings",
  globe: "IconGlobe",
  star: "IconStar",
  heart: "IconHeart",
  stethoscope: "IconStethoscope",
  graduation: "IconGraduateCap",
  code: "IconCode",
  rocket: "IconRocket",
  shield: "IconShield",
  flask: "IconLab",
  leaf: "IconTree",
  sparkles: "IconSparklesTwo",
  handshake: "IconHandshake",
  folder: "IconFolder1",
};

export interface PoolColorClasses {
  /** Icon/text tint. */
  text: string;
  /** Soft background behind the icon. */
  bg: string;
  border: string;
  /** Solid fill for a color swatch. */
  swatch: string;
}

// Full class strings: Tailwind only generates classes it finds verbatim.
export const POOL_COLORS: Record<PoolColorKey, PoolColorClasses> = {
  gray: {
    text: "text-gray-600 dark:text-gray-300",
    bg: "bg-gray-500/15",
    border: "border-gray-500/30",
    swatch: "bg-gray-500",
  },
  red: {
    text: "text-red-600 dark:text-red-400",
    bg: "bg-red-500/15",
    border: "border-red-500/30",
    swatch: "bg-red-500",
  },
  orange: {
    text: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-500/15",
    border: "border-orange-500/30",
    swatch: "bg-orange-500",
  },
  amber: {
    text: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-500/15",
    border: "border-amber-500/30",
    swatch: "bg-amber-500",
  },
  yellow: {
    text: "text-yellow-600 dark:text-yellow-400",
    bg: "bg-yellow-500/15",
    border: "border-yellow-500/30",
    swatch: "bg-yellow-400",
  },
  green: {
    text: "text-green-600 dark:text-green-400",
    bg: "bg-green-500/15",
    border: "border-green-500/30",
    swatch: "bg-green-500",
  },
  teal: {
    text: "text-teal-600 dark:text-teal-400",
    bg: "bg-teal-500/15",
    border: "border-teal-500/30",
    swatch: "bg-teal-500",
  },
  cyan: {
    text: "text-cyan-600 dark:text-cyan-400",
    bg: "bg-cyan-500/15",
    border: "border-cyan-500/30",
    swatch: "bg-cyan-500",
  },
  blue: {
    text: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-500/15",
    border: "border-blue-500/30",
    swatch: "bg-blue-500",
  },
  indigo: {
    text: "text-indigo-600 dark:text-indigo-400",
    bg: "bg-indigo-500/15",
    border: "border-indigo-500/30",
    swatch: "bg-indigo-500",
  },
  violet: {
    text: "text-violet-600 dark:text-violet-400",
    bg: "bg-violet-500/15",
    border: "border-violet-500/30",
    swatch: "bg-violet-500",
  },
  pink: {
    text: "text-pink-600 dark:text-pink-400",
    bg: "bg-pink-500/15",
    border: "border-pink-500/30",
    swatch: "bg-pink-500",
  },
};

type PoolLike = Pick<TalentPoolResponse, "kind" | "source" | "is_standard" | "icon" | "color">;

/** Icon and color a pool shows when none is set: by kind, then by source. */
export function defaultPoolAppearance(
  pool: Omit<PoolLike, "icon" | "color">,
): { icon: PoolIconKey; color: PoolColorKey } {
  if (pool.kind === "global") {
    return { icon: "globe", color: pool.is_standard ? "blue" : "indigo" };
  }
  if (pool.source === "ats") return { icon: "briefcase", color: "amber" };
  if (pool.source === "upload") return { icon: "folder", color: "violet" };
  return { icon: "users", color: "teal" };
}

/** A pool's chosen icon and color, each falling back to its default. */
export function poolAppearance(pool: PoolLike): {
  icon: IconName;
  color: PoolColorClasses;
} {
  const fallback = defaultPoolAppearance(pool);
  return {
    icon: POOL_ICONS[pool.icon ?? fallback.icon],
    color: POOL_COLORS[pool.color ?? fallback.color],
  };
}
